import { useListProjects } from '@repo/services'
import { useTranslate, type MessagesKeys } from '../../hooks'
import { globalConfig } from '../../utils'
import {
  AspectRatio,
  Badge,
  Button,
  Col,
  Icon,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
  Row,
  Skeleton,
  Typography,
} from '@repo/ui/components'

import type { FlatAsset, OrderBoxAssetSelectProps } from '../../components'
import { useDebounce } from '@repo/ui/hooks'
import { cn } from '@repo/ui/lib'
import Image from 'next/image'
import * as React from 'react'
import { OrderBoxAssetSelectItem } from '../../components'

const getLogoUrl = (url?: string) =>
  url ? `${globalConfig.baseImageUrl}${url}` : '/images/placeholder.webp'

export const AdvancedTradeBoxAssetSelect: React.FC<
  OrderBoxAssetSelectProps
> = ({
  selectedAsset,
  onChange,
  error,
  tokenClassName,
  iconClassName,
  containerClassName,
}) => {
  const { t } = useTranslate()
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebounce(search, 400) || ''
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useListProjects({
      skip: 0,
      limit: 10,
      // provinceID: 123,
      tradeStatus: 'active',
      ...(search && {
        q: debouncedSearch,
      }),
    })

  const handleLoadMore = React.useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const assets =
    React.useMemo(
      () =>
        data?.pages
          .flatMap((page) => page.data?.projects || [])
          .map((project) => ({
            id: project.id?.toString() || '',
            name: project.displayName || '',
            image: getLogoUrl(project?.logoURL) || '',
            price: project.price?.priceRange24h?.last || 0,
            pricePerMeter: project.price?.priceRange24h?.last || 0,
            about: project.description || '',
            province: project.location?.province?.name || '',
            county: project.location?.county?.name || '',
            city: project.location?.city?.name || '',
            district: project.location?.district?.name || '',
            type: (project.type as 'initial' | 'final') || 'initial',
            state: project.state || '',
            tradeStatus: project.tradeStatus || 'active',
            percentChange: project.price?.priceRange24h?.deltaPercent,
            description: project.description || '',
            usage: t(`filters_usage_${project.type}` as MessagesKeys) || '',
            galleryImages: (project.photos
              ?.map((photo) => getLogoUrl(photo.smallURL))
              .filter((url): url is string => Boolean(url)) || []) as string[],
            specs: [],
            totalArea: { m2: 0, cm2: 0 },
            offeredArea: { m2: 0, cm2: 0 },
            purchasableArea: { m2: 0, cm2: 0 },
            priceChartData: [],
            logoURL: project.logoURL,
            location: { lat: 0, lng: 0 },
            token: project.token || '',
          })),
      [data, t],
    ) || []

  const handleScroll = React.useCallback(() => {
    if (
      !scrollContainerRef.current ||
      !hasNextPage ||
      isFetchingNextPage ||
      !handleLoadMore
    ) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50

    if (isNearBottom) {
      handleLoadMore()
    }
  }, [hasNextPage, isFetchingNextPage, handleLoadMore])

  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer && open) {
      const timer = setTimeout(() => {
        scrollContainer.addEventListener('scroll', handleScroll)
      }, 100)

      return () => {
        clearTimeout(timer)
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll, open])

  const handleAssetSearch = (value: string) => {
    setSearch(value)
  }

  const handleOpenChange = (open: boolean) => {
    setOpen(open)
    if (open) {
      setSearch('')
    }
  }

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalTrigger asChild>
        <Button
          type='button'
          variant='transparent'
          width='full'
          className={cn(
            'justify-between rounded-md p-md gap-md text-neutral-main border',
            error ? 'border-negative-surface-100' : 'border-neutral-surface',
            containerClassName,
          )}
        >
          <Row className='gap-md w-full'>
            <div className={cn('w-12 h-12', iconClassName)}>
              <AspectRatio className=' aspect-1/1 rounded-2xs'>
                <Image
                  src={
                    getLogoUrl(selectedAsset?.logoURL) ||
                    '/images/placeholder.webp'
                  }
                  alt='Photo of property'
                  fill
                  sizes='48px'
                  priority
                  className='rounded-sm object-cover'
                />
              </AspectRatio>
            </div>
            <Col className='flex-1 items-start gap-xs'>
              <Row className='gap-2xs'>
                <Typography
                  size='sm'
                  truncate
                  className={cn(
                    'max-w-[170px] md:max-w-[200px]',
                    tokenClassName,
                  )}
                >
                  {selectedAsset?.name || t('common_select_asset')}
                </Typography>
                {selectedAsset && (
                  <Badge
                    size='sm'
                    variant='liquid'
                    color={'base'}
                    className='ml-2'
                  >
                    {t(`common_${selectedAsset?.state}` as MessagesKeys)}
                  </Badge>
                )}
              </Row>

              <Typography
                size='xs'
                color='text-neutral-surface-60'
                className='font-[AzarMehr]'
              >
                {selectedAsset?.token || ''}
              </Typography>
            </Col>
            <Icon name='ChevronDown' size='sm' />
          </Row>
        </Button>
      </ModalTrigger>
      <ModalContent className='sm:max-w-[480px] p-0 py-md'>
        <ModalHeader>
          <ModalTitle>{t('common_asset_select')}</ModalTitle>
        </ModalHeader>
        <div className='px-md'>
          <Input
            value={search}
            onChange={(e) => handleAssetSearch(e.target.value)}
            placeholder={t('common_search')}
            icon={
              <Icon
                name='SearchLine'
                size='sm'
                color='fill-neutral-surface-40'
              />
            }
            endIcon={
              search && (
                <Icon
                  name='XmarkCircle'
                  size='sm'
                  color='fill-neutral-surface-40'
                  onClick={() => setSearch('')}
                />
              )
            }
            inputSize='md'
            autoComplete='off'
          />
        </div>
        <div
          ref={scrollContainerRef}
          className='max-h-[60vh] overflow-y-auto mt-md'
          onScroll={handleScroll}
        >
          <Col className='gap-md'>
            {assets.map((asset) => (
              <React.Fragment key={asset.id}>
                <OrderBoxAssetSelectItem
                  asset={asset as unknown as FlatAsset}
                  onClick={() => {
                    onChange(asset as unknown as FlatAsset)
                    setOpen(false)
                  }}
                />
              </React.Fragment>
            ))}

            {(isLoading || isFetchingNextPage) && (
              <Col className='gap-md px-md'>
                <Skeleton className='w-full h-36' />
                <Skeleton className='w-full h-36' />
                <Skeleton className='w-full h-36' />
                <Skeleton className='w-full h-36' />
                <Skeleton className='w-full h-36' />
              </Col>
            )}
          </Col>
        </div>
      </ModalContent>
    </Modal>
  )
}
